import { typographyTokens } from '../../theme/typographyTokens';
import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Grid, Container, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material';
import { transformQuillListToNested, nestedListStyles } from '../../utils/quillHtmlTransform';
import { SEPSearchResult, CreateSEPBody } from '../../../shared/types/SEP';
import {
  useCreateSepMutation,
  useUpdateSepMutation,
} from '../../services/sepSlice';
import { useNavigate, useLocation } from 'react-router';
import { useGetDataFieldTemplatesQuery } from '../../services/dataFieldsSlice';
import { useGetWorkflowDataFieldTemplateQuery } from '../../../frontend/services/knockoutSlice';
import { DataFieldOption } from '../../../shared/types/DataField';
import Tile from '../../components/Tile';
import { LoadingContainer } from '../../containers/LoadingContainer';

export const CreateSEP = (SEPSearch: SEPSearchResult) => {
  const { count, seps } = SEPSearch;
  const navigate = useNavigate();
  const theme = useTheme();
  const [createSep, { isLoading: isCreating }] = useCreateSepMutation();
  const [updateSep, { isLoading: isUpdating }] = useUpdateSepMutation();
  const SEPStarted = count === 1;
  const location = useLocation();
  const [sep, setSep] = useState<CreateSEPBody>({
    name: '',
    description: '',
  });
  const { data: getTemplateScreen } = useGetWorkflowDataFieldTemplateQuery();
  const { data: getTemplateDataFieldOption } = useGetDataFieldTemplatesQuery();
  const [showLoader, setShowLoader] = useState(false)
  const getWorkFlowData = getTemplateDataFieldOption?.find((data: any) => data?.name === 'Workflow data field');
  const workFlowHeading = getTemplateScreen?.find((data: any) => data?.name.includes('Solution Enablement Process'));
  const [optionsList, setOptionsList] = useState(getWorkFlowData?.dataFieldOptions)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setSep({ ...sep, [name]: value });
  };

  const handleCreate = async () => {
    const { name, description } = sep;
    const selectedOption = optionsList?.find((opt: any) => opt.selected)?.value;
    setShowLoader(true)
    try {
    if(selectedOption !== 'Do I need an SEP?') {
    const create = await createSep({ name:'', description:'', workFlowType: selectedOption });
    if ('data' in create)
      navigate(`/knockout/${create.data.id}`, { state: location.pathname });
    } } catch (error) {
    console.error('error while creating SEP', error)
  } finally {
    setShowLoader(false)
  }
  };

  const handleChangeDescription = (e: string): void => {
    setSep({ ...sep, description: e });
  };

  const handleUpdate = async () => {
    const id = SEPSearch.seps[0].id;
    const selectedOption = optionsList?.find((opt: any) => opt.selected)?.value
    const updatedWorkflowType = {workFlowType: selectedOption}
    if(selectedOption !== 'Do I need an SEP?') {
    const update = await updateSep({ id, body: updatedWorkflowType }).unwrap();
    if ('id' in update)
      navigate(`/knockout/${update.id}`, { state: location.pathname });
    }
  };

  const handleClick = () => {
    SEPStarted ? handleUpdate() : handleCreate();
  };

  useEffect(() => {
    if (!SEPStarted) return;
    if (SEPStarted) {
      // setSep({
      //   name: seps[0].name,
      //   description: seps[0].description,
      // });
      
    }
    //eslint-disable-next-line
  }, [seps[0]?.name, seps[0]?.description, SEPStarted]);

  useEffect(()=>{
    setOptionsList(getWorkFlowData?.dataFieldOptions)
  }, [getWorkFlowData])

  const handleSelect = (index:any) => {
    const newOptions = optionsList?.map((opt, i) => 
    i === index ? {...opt, selected: true} : {...opt, selected: false}
    );
    setOptionsList(newOptions)
  }

  const anyOptionSelected = optionsList?.some((option: any) => option.selected)

  return (
    <>
    { showLoader ? (
    <LoadingContainer>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="60vh"
            width="100%"
          >
            <CircularProgress size={100} />
            <Typography variant="h5" sx={{ fontWeight: typographyTokens.fontWeights.bold, mt: 5 }}>
              Setting up your new SEP...
            </Typography>
          </Box>
        </LoadingContainer>
      ) : (
        <Box sx={{ minHeight: 'calc(100vh - 56px)', backgroundColor: '#f5f5f5', pt: 2.25 }}>
        <Typography sx={{ font: 'normal normal 600 22px/30px Open Sans', textAlign: 'center', color: '#222' }}>
          {workFlowHeading?.name}
        </Typography>
        <Typography
          mt={1.5}
          mb={3.5}
          sx={{
            font: 'normal normal normal 14px/20px Open Sans',
            color: theme.palette.gray.solidGray,
            maxWidth: 600,
            wordBreak: 'break-word',
            textAlign: 'center',
            marginLeft: 'auto',
            marginRight: 'auto',
            ...nestedListStyles,
          }}
        >
          <span
            className="knockout-description"
            dangerouslySetInnerHTML={{
              __html: transformQuillListToNested(workFlowHeading?.description ?? ''),
            }}
          />
        </Typography>
      <Container sx={{ mt: 2 }} maxWidth="lg">
        <Grid container spacing={3} alignItems="stretch" justifyContent="center">
        {optionsList?.map((option: DataFieldOption, index: any) => {
          return (
            <Tile
              variant="workflow"
              length={optionsList?.length || 2}
              field={option}
              onClick={() => handleSelect(index)}
              onContinue={handleClick}
              selected={option?.selected}
              key={option.id}
            />
          );
        })} 
        
         </Grid>
      </Container>

        </Box>
      )}
    </>
  );
};
